// cleancode-ui/src/contexts/EnvironmentContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';

const EnvironmentContext = createContext();

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

// Function to determine the initial API base URL based on where the app is running
const getInitialApiBaseUrl = () => {
  // Check for environment override in localStorage first
  const envOverride = localStorage.getItem('API_ENVIRONMENT');
  if (envOverride) {
    if (envOverride === 'beta') {
      return 'https://api-beta.cleancodeai.co';
    } else if (envOverride === 'live') {
      return 'https://api.cleancodeai.co';
    } else if (envOverride === 'sandbox') {
      return 'http://localhost:8888';
    }
  }
  
  // If we're in development (localhost:3000), use localhost:8888 (sandbox)
  if (window.location.hostname === 'localhost' && window.location.port === '3000') {
    return 'http://localhost:8888';
  }
  
  // If we're running on cleancode.ai domain, use production API (live)
  if (window.location.hostname === 'cleancode.ai') {
    return 'https://api.cleancodeai.co';
  }
  
  // If we're running on beta domain, use beta API
  if (window.location.hostname.includes('beta') || window.location.hostname.includes('staging')) {
    return 'https://api-beta.cleancodeai.co';
  }
  
  // Default fallback - try to detect from current domain
  if (window.location.protocol === 'https:') {
    return `https://api-${window.location.hostname}`;
  }
  
  // Fallback to localhost for development (sandbox)
  return 'http://localhost:8888';
};

export const EnvironmentProvider = ({ children }) => {
  const [environment, setEnvironment] = useState('sandbox');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiBaseUrl, setApiBaseUrl] = useState(getInitialApiBaseUrl());

  const fetchEnvironmentInfo = async () => {
    try {
      // Use the current API base URL for environment info fetch
      const response = await fetch(`${apiBaseUrl}/billing/environment-info`);
      if (!response.ok) {
        throw new Error('Failed to fetch environment info');
      }
      const data = await response.json();
      setEnvironment(data.environment);
      setApiBaseUrl(getApiUrl(data.environment));
    } catch (err) {
      setError(err.message);
      console.error('Error fetching environment info:', err);
    } finally {
      setLoading(false);
    }
  };

  const switchEnvironment = async (newEnvironment) => {
    try {
      const response = await fetch(`${apiBaseUrl}/billing/switch-environment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ environment: newEnvironment }),
      });

      if (!response.ok) {
        throw new Error('Failed to switch environment');
      }

      const data = await response.json();
      setEnvironment(newEnvironment);
      setApiBaseUrl(getApiUrl(newEnvironment));
      
      // Show notification about server restart
      if (data.note) {
        alert(`Environment switched to ${newEnvironment}. ${data.note}`);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error switching environment:', err);
    }
  };

  useEffect(() => {
    fetchEnvironmentInfo();
  }, []); // Only run once on mount

  const value = {
    environment,
    loading,
    error,
    switchEnvironment,
    isSandbox: environment === 'sandbox' || environment === 'beta',
    isLive: environment === 'live',
    refreshEnvironment: fetchEnvironmentInfo,
    apiBaseUrl
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};
